import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { X, RefreshCw, Maximize2, Minimize2, Signal, SignalZero, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import 'leaflet/dist/leaflet.css';

// Custom car emoji marker icon
const createCarIcon = () => {
  return L.divIcon({
    html: `<div style="font-size: 32px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)); transform: translate(-50%, -50%);">🚗</div>`,
    className: 'car-marker-icon',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });
};

// Component to auto-center map on position changes
function MapUpdater({ position, shouldCenter }) {
  const map = useMap();
  
  useEffect(() => {
    if (position && shouldCenter) {
      map.setView([position.latitude, position.longitude], map.getZoom(), {
        animate: true,
        duration: 0.5
      });
    }
  }, [position, shouldCenter, map]);
  
  return null;
}

export default function LiveDriverMap({ driverId, driverName, onClose }) {
  const { token } = useAuth();
  const [position, setPosition] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoCenter, setAutoCenter] = useState(true);
  const [updateCount, setUpdateCount] = useState(0);
  const mapRef = useRef(null);
  const intervalRef = useRef(null);

  const carIcon = createCarIcon();

  // Fetch driver location
  const fetchDriverLocation = async () => {
    try {
      const response = await axios.get(`${API}/drivers/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const drivers = response.data.drivers || [];
      const driver = drivers.find(d => d.driver_id === driverId);
      
      if (driver) {
        setPosition({
          latitude: driver.latitude,
          longitude: driver.longitude,
          accuracy: driver.accuracy,
          speed: driver.speed,
          heading: driver.heading
        });
        setLastUpdated(new Date(driver.last_updated));
        setIsConnected(true);
        setUpdateCount(prev => prev + 1);
      } else {
        setIsConnected(false);
      }
    } catch (error) {
      console.error('Error fetching driver location:', error);
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  };

  // Start polling for updates
  useEffect(() => {
    fetchDriverLocation();
    
    // Update every 3 seconds for real-time tracking
    intervalRef.current = setInterval(fetchDriverLocation, 3000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [driverId, token]);

  // Format time
  const formatTime = (date) => {
    if (!date) return 'Unknown';
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    
    if (diffSecs < 5) return 'Just now';
    if (diffSecs < 60) return `${diffSecs}s ago`;
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;
    return date.toLocaleTimeString();
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Loading state
  if (loading) {
    return (
      <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'relative'} bg-slate-900 rounded-xl overflow-hidden`}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center text-white">
            <div className="animate-spin h-10 w-10 border-3 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Connecting to {driverName}...</p>
          </div>
        </div>
      </div>
    );
  }

  // No position available
  if (!position) {
    return (
      <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'relative'} bg-slate-900 rounded-xl overflow-hidden`}>
        <div className="absolute top-4 right-4 z-[1000] flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            className="h-8 bg-white/10 hover:bg-white/20 text-white border-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center justify-center h-96">
          <div className="text-center text-white">
            <SignalZero className="h-16 w-16 mx-auto mb-4 text-red-400" />
            <p className="text-xl font-semibold mb-2">No GPS Signal</p>
            <p className="text-slate-400">{driverName} hasn't shared their location yet</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchDriverLocation}
              className="mt-4 text-white border-white/30 hover:bg-white/10"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`${isFullscreen ? 'fixed inset-0 z-50' : 'relative'} bg-slate-900 rounded-xl overflow-hidden`}
      style={{ height: isFullscreen ? '100vh' : '500px' }}
      data-testid="live-driver-map"
    >
      {/* Header Overlay */}
      <div className="absolute top-0 left-0 right-0 z-[1000] bg-gradient-to-b from-slate-900/90 to-transparent p-4">
        <div className="flex items-start justify-between gap-2">
          {/* Driver Info */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-xl flex-shrink-0">
              🚗
            </div>
            <div className="min-w-0">
              <h3 className="text-white font-semibold truncate" data-testid="tracked-driver-name">{driverName}</h3>
              <div className="flex items-center gap-2 text-sm">
                <span className={`flex items-center gap-1 ${isConnected ? 'text-emerald-400' : 'text-red-400'}`}>
                  {isConnected ? <Signal className="h-3 w-3" /> : <SignalZero className="h-3 w-3" />}
                  {isConnected ? 'Live' : 'Disconnected'}
                </span>
                <span className="text-slate-400 hidden sm:inline" data-testid="last-update-time">
                  • {formatTime(lastUpdated)}
                </span>
              </div>
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (position) {
                  window.open(`https://www.google.com/maps/search/?api=1&query=${position.latitude},${position.longitude}`, '_blank');
                }
              }}
              className="h-8 px-2 sm:px-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border-0 text-xs sm:text-sm"
              title="Open in Google Maps"
              data-testid="open-google-maps-btn"
            >
              <ExternalLink className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Google Maps</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAutoCenter(!autoCenter)}
              className={`h-8 px-2 ${autoCenter ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-white'} border-0 text-xs`}
              title={autoCenter ? 'Auto-center ON' : 'Auto-center OFF'}
            >
              {autoCenter ? '📍' : '✋'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={toggleFullscreen}
              className="h-8 px-2 bg-white/10 hover:bg-white/20 text-white border-0"
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              className="h-8 px-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Map */}
      <MapContainer
        ref={mapRef}
        center={[position.latitude, position.longitude]}
        zoom={16}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapUpdater position={position} shouldCenter={autoCenter} />
        
        <Marker 
          position={[position.latitude, position.longitude]} 
          icon={carIcon}
        >
          <Popup>
            <div className="text-center p-2">
              <div className="text-2xl mb-1">🚗</div>
              <p className="font-semibold">{driverName}</p>
              <p className="text-xs text-slate-500">
                {formatTime(lastUpdated)}
              </p>
              {position.speed && position.speed > 0 && (
                <p className="text-xs text-blue-600 mt-1">
                  {Math.round(position.speed * 3.6)} km/h
                </p>
              )}
            </div>
          </Popup>
        </Marker>
      </MapContainer>

      {/* Bottom Stats Bar */}
      <div className="absolute bottom-0 left-0 right-0 z-[1000] bg-gradient-to-t from-slate-900/90 to-transparent p-4">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4 text-white">
            {position.speed !== undefined && (
              <div className="flex items-center gap-1">
                <span className="text-slate-400">Speed:</span>
                <span className="font-medium">{Math.round((position.speed || 0) * 3.6)} km/h</span>
              </div>
            )}
            {position.accuracy && (
              <div className="flex items-center gap-1">
                <span className="text-slate-400">Accuracy:</span>
                <span className="font-medium">±{Math.round(position.accuracy)}m</span>
              </div>
            )}
          </div>
          <div className="text-slate-400 text-xs">
            Updates: {updateCount} • Refresh: 3s
          </div>
        </div>
      </div>
    </div>
  );
}
